// Lightweight analytics helper — fires events to /api/track.
// Fire-and-forget: never throws, never blocks UI.
// Auto-captures: session_id (persisted), page, referrer, UTM params.
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TRACK_URL = `${BACKEND_URL}/api/track`;
const SESSION_KEY = "pb_analytics_sid";
const UTM_KEY = "pb_analytics_utm";

// Persist a stable session id per browser tab session
function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return null;
  }
}

// Capture UTM params on first landing and persist for attribution across signup
function captureUtm() {
  try {
    const url = new URL(window.location.href);
    const utm = {
      utm_source: url.searchParams.get("utm_source"),
      utm_medium: url.searchParams.get("utm_medium"),
      utm_campaign: url.searchParams.get("utm_campaign"),
    };
    if (utm.utm_source || utm.utm_medium || utm.utm_campaign) {
      localStorage.setItem(UTM_KEY, JSON.stringify(utm));
    }
  } catch {}
}

function getUtm() {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// Fire an event — never awaits, never throws
export function track(event, properties = {}) {
  try {
    const utm = getUtm();
    const payload = {
      event,
      properties,
      session_id: getSessionId(),
      page: typeof window !== "undefined" ? window.location.pathname : null,
      referrer: typeof document !== "undefined" ? document.referrer || null : null,
      utm_source: utm.utm_source || null,
      utm_medium: utm.utm_medium || null,
      utm_campaign: utm.utm_campaign || null,
    };
    // fire-and-forget with tiny timeout so slow networks don't queue up
    axios.post(TRACK_URL, payload, { timeout: 4000 }).catch(() => {});
  } catch {}
}

// Track a page view — call from route change listener
export function trackPageView(pathname) {
  track("page_view", { path: pathname });
}

// Bootstrap — call once at app mount
export function initAnalytics() {
  captureUtm();
  getSessionId();
}
